import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { map, Observable } from 'rxjs';

import {
    FiscalAuthorizationDownload,
    FiscalAuthorizationPreview,
    FiscalBusinessPayload,
    FiscalChangeRequestPayload,
    FiscalOnboardingResponse,
    FiscalOnboardingState,
    FiscalSignatureDraft,
    FiscalSignaturePayload,
} from '../models';

/**
 * Solo HTTP del onboarding fiscal (`/api/fiscal-onboarding`, auth-only en el api; la visibilidad la
 * controla el flag PostHog `fiscal-recaudos`). El estado vive en `FiscalOnboardingStateService`.
 */
@Injectable({ providedIn: 'root' })
export class FiscalOnboardingApiService {
    private readonly base = `${environment.API_URL}/fiscal-onboarding`;

    constructor(private http: HttpClient) {}

    getState$(): Observable<FiscalOnboardingState> {
        return this.http.get<FiscalOnboardingResponse<FiscalOnboardingState>>(this.base).pipe(map(res => res.data));
    }

    saveBusiness$(payload: FiscalBusinessPayload): Observable<FiscalOnboardingState> {
        return this.http.put<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/business`, payload).pipe(map(res => res.data));
    }

    addPartner$(): Observable<FiscalOnboardingState> {
        return this.http.post<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/partners`, {}).pipe(map(res => res.data));
    }

    removePartner$(partnerId: string): Observable<FiscalOnboardingState> {
        return this.http.delete<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/partners/${encodeURIComponent(partnerId)}`).pipe(map(res => res.data));
    }

    /** Subida con eventos de progreso (campo `file`). */
    uploadDocument$(docKey: string, file: File): Observable<HttpEvent<FiscalOnboardingResponse<FiscalOnboardingState>>> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/documents/${encodeURIComponent(docKey)}`, formData, { reportProgress: true, observe: 'events' });
    }

    deleteDocument$(docKey: string): Observable<FiscalOnboardingState> {
        return this.http.delete<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/documents/${encodeURIComponent(docKey)}`).pipe(map(res => res.data));
    }

    submit$(): Observable<FiscalOnboardingResponse<FiscalOnboardingState>> {
        return this.http.post<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/submit`, { confirmed: true });
    }

    resubmit$(): Observable<FiscalOnboardingResponse<FiscalOnboardingState>> {
        return this.http.post<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/resubmit`, {});
    }

    /**
     * Vista previa de la solicitud. Con firma y/o sello en el borrador va por `POST` multipart y el api
     * los embebe como quedarán al firmar; sin imágenes, el `GET` de la solicitud en blanco.
     */
    previewAuthorization$(draft?: FiscalSignatureDraft | null): Observable<FiscalAuthorizationPreview> {
        const url = `${this.base}/authorization/preview`;
        if (!draft?.signature && !draft?.seal) {
            return this.http.get<FiscalOnboardingResponse<FiscalAuthorizationPreview>>(url).pipe(map(res => res.data));
        }
        const formData = new FormData();
        if (draft.signature) formData.append('signature', draft.signature, 'firma.png');
        if (draft.seal) formData.append('seal', draft.seal, 'sello.png');
        return this.http.post<FiscalOnboardingResponse<FiscalAuthorizationPreview>>(url, formData).pipe(map(res => res.data));
    }

    /** Multipart `signature` + `seal` (PNG/JPG). */
    signAuthorization$(payload: FiscalSignaturePayload): Observable<FiscalOnboardingResponse<FiscalOnboardingState>> {
        const formData = new FormData();
        formData.append('signature', payload.signature, 'firma.png');
        formData.append('seal', payload.seal, 'sello.png');
        return this.http.post<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/authorization/sign`, formData);
    }

    downloadAuthorization$(): Observable<FiscalAuthorizationDownload> {
        return this.http.get<FiscalOnboardingResponse<FiscalAuthorizationDownload>>(`${this.base}/authorization/download`).pipe(map(res => res.data));
    }

    // ── Solicitud de cambio de perfil (`/change-request`, expediente activo, solo dueño) ──────────

    /** Abre el borrador (copia del negocio actual) con qué se cambia y el motivo. */
    openChangeRequest$(payload: FiscalChangeRequestPayload): Observable<FiscalOnboardingState> {
        return this.http.post<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/change-request`, payload).pipe(map(res => res.data));
    }

    /** Actualiza qué se cambia y el motivo de un borrador ya abierto. */
    updateChangeRequest$(payload: FiscalChangeRequestPayload): Observable<FiscalOnboardingState> {
        return this.http.put<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/change-request`, payload).pipe(map(res => res.data));
    }

    saveChangeBusiness$(payload: FiscalBusinessPayload): Observable<FiscalOnboardingState> {
        return this.http.put<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/change-request/business`, payload).pipe(map(res => res.data));
    }

    addChangePartner$(): Observable<FiscalOnboardingState> {
        return this.http.post<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/change-request/partners`, {}).pipe(map(res => res.data));
    }

    removeChangePartner$(partnerId: string): Observable<FiscalOnboardingState> {
        return this.http.delete<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/change-request/partners/${encodeURIComponent(partnerId)}`).pipe(map(res => res.data));
    }

    /** Reemplazo de un documento en el borrador (progreso como la subida normal). */
    uploadChangeDocument$(docKey: string, file: File): Observable<HttpEvent<FiscalOnboardingResponse<FiscalOnboardingState>>> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/change-request/documents/${encodeURIComponent(docKey)}`, formData, {
            reportProgress: true,
            observe: 'events',
        });
    }

    deleteChangeDocument$(docKey: string): Observable<FiscalOnboardingState> {
        return this.http.delete<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/change-request/documents/${encodeURIComponent(docKey)}`).pipe(map(res => res.data));
    }

    submitChangeRequest$(): Observable<FiscalOnboardingResponse<FiscalOnboardingState>> {
        return this.http.post<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/change-request/submit`, { confirmed: true });
    }

    /** Cerrar el asistente sin enviar: descarta el borrador y sus reemplazos. */
    discardChangeRequest$(): Observable<FiscalOnboardingState> {
        return this.http.delete<FiscalOnboardingResponse<FiscalOnboardingState>>(`${this.base}/change-request`).pipe(map(res => res.data));
    }
}
